import { Vector3, type PerspectiveCamera } from 'three';
import type { MirrorShard } from './MirrorShard.js';

/**
 * Moves the shards like orbital debris rather than like dust.
 *
 * A Brownian random walk was explicitly ruled out, and rightly so: integrated
 * white noise has no persistence, so shards jitter in place and never appear to
 * be *going* anywhere. Instead each shard carries four superposed terms, all
 * continuous and all slow:
 *
 *   1. a constant linear velocity — the sense of travel
 *   2. low-frequency sine wander on three axes — the sense of tumbling through
 *      an uneven medium, with periods of 20-90 s that share no common multiple
 *   3. a weak tangential push plus a radial spring — a lazy, non-closing orbit
 *   4. a dwell-driven eviction from the centre of frame
 *
 * Term 4 is what satisfies "must not block the centre for long". A hard exclusion
 * zone would forbid the foreground crossings the brief explicitly permits, so
 * instead the push scales with how long a shard has *already* been in the way:
 * passing through is free, parking is not.
 */

export interface MirrorDriftOptions {
  /** Half-angle of the protected central cone, radians. */
  readonly centralConeAngle?: number;
  /** Seconds a shard may obscure the centre before eviction begins. */
  readonly dwellGrace?: number;
  /** Seconds at which the eviction push reaches full strength. */
  readonly dwellFull?: number;
  /** Hard ceiling on drift speed, world units per second. */
  readonly maxSpeed?: number;
  readonly nearZ?: number;
  readonly farZ?: number;
}

const scratchDrift = new Vector3();
const scratchRadial = new Vector3();

export class MirrorDriftController {
  private readonly centralConeAngle: number;
  private readonly dwellGrace: number;
  private readonly dwellFull: number;
  private readonly maxSpeed: number;
  private readonly nearZ: number;
  private readonly farZ: number;

  constructor(options: MirrorDriftOptions = {}) {
    this.centralConeAngle = options.centralConeAngle ?? (13 * Math.PI) / 180;
    this.dwellGrace = options.dwellGrace ?? 4;
    this.dwellFull = options.dwellFull ?? 9;
    this.maxSpeed = options.maxSpeed ?? 0.55;
    this.nearZ = options.nearZ ?? 12;
    this.farZ = options.farZ ?? -52;
  }

  update(shards: readonly MirrorShard[], delta: number, time: number, camera: PerspectiveCamera): void {
    for (const shard of shards) {
      this.advance(shard, delta, time, camera);
      shard.syncTransform();
    }
  }

  private advance(
    shard: MirrorShard,
    delta: number,
    time: number,
    camera: PerspectiveCamera,
  ): void {
    const state = shard.state;
    const position = state.position;

    scratchDrift.copy(state.velocity);

    // (2) Low-frequency wander. Sines, not noise: continuous, bounded, and
    // repeatable from the seed.
    scratchDrift.x +=
      Math.sin(time * state.wanderFrequency.x + state.wanderPhase.x) * state.wanderAmplitude;
    scratchDrift.y +=
      Math.sin(time * state.wanderFrequency.y + state.wanderPhase.y) * state.wanderAmplitude;
    scratchDrift.z +=
      Math.sin(time * state.wanderFrequency.z + state.wanderPhase.z) * state.wanderAmplitude * 0.6;

    // (3) A lazy orbit around the *frame*, not around the world origin.
    //
    // The anchor is resolved to world units every frame from the shard's current
    // distance to the camera and the camera's own aspect, so a shard keeps the
    // same place in the composition whether it is near or far and whatever the
    // window shape is. Depth then only changes how large it appears, which is
    // what "spread through depth, arranged around the edge" actually means.
    const distanceToCamera = Math.max(camera.position.distanceTo(position), 0.001);
    const halfHeight = Math.tan(((camera.fov * Math.PI) / 180) / 2) * distanceToCamera;
    const halfWidth = halfHeight * camera.aspect;

    state.anchorAngle += state.anchorAngularSpeed * delta;
    // The orbit breathes so it never retraces the same ellipse.
    const breath = 1 + Math.sin(time * 0.017 + state.wanderPhase.x) * 0.07;
    const reach = state.anchorRadius * breath * 0.88;
    const targetX = Math.cos(state.anchorAngle) * reach * halfWidth;
    const targetY = Math.sin(state.anchorAngle) * reach * halfHeight;

    // A weak spring, so wander and linear velocity stay visible on top of it.
    scratchDrift.x += (targetX - position.x) * 0.13;
    scratchDrift.y += (targetY - position.y) * 0.13;

    // (4) Central eviction. The cone is angular, so the world-space clearance a
    // shard needs grows with its distance from the camera — which is what keeps
    // the *screen* centre clear rather than the world origin.
    const planarRadius = Math.hypot(position.x, position.y);
    const coneRadius = Math.tan(this.centralConeAngle) * distanceToCamera;
    if (planarRadius < coneRadius) {
      state.centralDwell += delta;
      const overstay = Math.max(state.centralDwell - this.dwellGrace, 0);
      const urgency = Math.min(overstay / Math.max(this.dwellFull - this.dwellGrace, 0.001), 1);
      if (urgency > 0) {
        scratchRadial.set(position.x, position.y, 0);
        if (scratchRadial.lengthSq() < 1e-6) {
          // Dead centre has no outward direction; nudge along a stable axis.
          scratchRadial.set(1, 0, 0);
        }
        scratchRadial.normalize();
        scratchDrift.addScaledVector(scratchRadial, urgency * 0.9);
      }
    } else {
      // Recovers faster than it accumulates, so a shard that leaves is not
      // penalised on its next pass.
      state.centralDwell = Math.max(state.centralDwell - delta * 1.6, 0);
    }

    // No fast movement, ever. Clamping the composed vector rather than each term
    // keeps the terms' relative weights intact.
    if (scratchDrift.length() > this.maxSpeed) {
      scratchDrift.setLength(this.maxSpeed);
    }

    position.addScaledVector(scratchDrift, delta);
    this.constrainDepth(state, delta);

    state.rotation.x += state.angularVelocity.x * delta;
    state.rotation.y += state.angularVelocity.y * delta;
    state.rotation.z += state.angularVelocity.z * delta;
  }

  /**
   * Soft depth containment. Springs rather than clamps, so a shard eases back
   * into the volume instead of visibly striking an invisible wall.
   *
   * Only depth is bounded here; lateral position is governed by the screen-space
   * anchor, which cannot send a shard out of frame in the first place.
   */
  private constrainDepth(state: { position: Vector3; velocity: Vector3 }, delta: number): void {
    const position = state.position;

    if (position.z > this.nearZ) {
      position.z += (this.nearZ - position.z) * Math.min(delta * 0.8, 1);
      state.velocity.z = -Math.abs(state.velocity.z);
    } else if (position.z < this.farZ) {
      position.z += (this.farZ - position.z) * Math.min(delta * 0.8, 1);
      state.velocity.z = Math.abs(state.velocity.z);
    }
  }
}
